import { RelatedRecord, RelatedTableRowAction } from "../relatedTableActions.types";
import { prepareAutomationCommunication } from "../automation/executions/prepareAutomationCommunication";

// The services are loaded when an action runs, so registering every page doesn't pull
// the automation graph into each one.

/** The fixture's readiness cards as they now stand, or null if they can't be read. */
export async function readReadinessRows(
  globalDispatch: unknown,
  fixtureId: string
): Promise<RelatedRecord[] | null> {
  const { GetAutomationFixtureReadinessRows } = await import(
    "../automation/automationService"
  );
  const response = await GetAutomationFixtureReadinessRows(globalDispatch, fixtureId);

  return Array.isArray(response?.data) ? (response.data as RelatedRecord[]) : null;
}

const failed = (result: { response?: unknown; status?: number } | undefined) =>
  Boolean(result?.response || (result?.status && result.status >= 400));

/**
 * As the legacy readiness card had it: a copy that needs publishing is opened to be
 * finished; otherwise the content is prepared, which opens the copy it makes.
 */
const updateContent: RelatedTableRowAction = {
  id: "update-content",
  label: "Update content",
  variant: "primary",
  visible: (row) => row.needspublishing === true || row.canprepare === true,
  run: async ({ globalDispatch, parentId, row, navigate }) => {
    if (row.needspublishing === true) {
      navigate(`/email-communication?id=${row.copiedcommunicationid}`);

      return null;
    }

    const result = await prepareAutomationCommunication({
      automationId: row.automationid,
      dispatch: globalDispatch as null,
      fixtureId: parentId,
      navigate,
    });

    return result.redirected ? null : readReadinessRows(globalDispatch, parentId);
  },
};

const sendNow: RelatedTableRowAction = {
  id: "send-now",
  label: "Send now",
  variant: "outline-primary",
  visible: (row) => row.cansendnow === true,
  confirmation: () => ({
    title: "Send automation now",
    confirmText: "Send now",
    message:
      "Update this automation execution send time to now? The automation agent can pick it up on its next poll.",
  }),
  run: async ({ globalDispatch, parentId, row }) => {
    const { SendAutomationExecutionNow } = await import("../automation/automationService");
    const result = await SendAutomationExecutionNow(globalDispatch, row.executionid);

    if (failed(result)) {
      throw result;
    }

    return readReadinessRows(globalDispatch, parentId);
  },
};

const cancelSend: RelatedTableRowAction = {
  id: "cancel-send",
  label: "Cancel send",
  variant: "outline-danger",
  visible: (row) => row.cancancel === true,
  confirmation: (row) => ({
    title: "Cancel automation send",
    confirmText: "Cancel send",
    message: `Are you sure you want to cancel the send for ${row.name}?`,
  }),
  run: async ({ globalDispatch, parentId, row }) => {
    const { CancelAutomationExecution } = await import("../automation/automationService");
    const result = await CancelAutomationExecution(globalDispatch, row.executionid);

    if (failed(result)) {
      throw result;
    }

    return readReadinessRows(globalDispatch, parentId);
  },
};

export const fixtureReadinessRowActions = [cancelSend, updateContent, sendNow];
